import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import './TermsPage.css';

const TermsPage = () => {
  const currentYear = new Date().getFullYear();
  const [scrolled, setScrolled] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return false; // default to light for terms page
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check for dark mode preference using the app's custom system
    const checkDarkMode = () => {
      const storedTheme = localStorage.getItem("theme");
      const isDark = storedTheme ? storedTheme === "dark" : false; // default to light for terms page
      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to localStorage (when user toggles theme in navbar)
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        checkDarkMode();
      }
    };

    // Listen for changes to data-bs-theme attribute
    const observer = new MutationObserver(() => {
      checkDarkMode();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme']
    });

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [scrolled]);

  // Theme toggle handler
  const handleThemeToggle = () => {
    setDarkMode(prev => !prev);
  };

  // Handle theme changes
  useEffect(() => {
    document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    setIsDarkMode(darkMode);
  }, [darkMode]);

  const navbarShouldBeScrolled = scrolled || navbarExpanded;

  return (
    <div className="terms-page">
      <PageTitle title="Terms of Service" description="Terms of service and usage agreement for MedHive" />
      {/* Navigation Bar */}
      <nav className={`navbar custom-navbar navbar-expand-lg navbar-light fixed-top${navbarShouldBeScrolled ? ' scrolled' : ''}`}
        style={{ zIndex: 1050 }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
            <img src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"} alt="brand logo" style={{ height: "16px"}}/>

            {/*<img
              src={navbarShouldBeScrolled ? "/images/logo5.svg" : "/images/logo4.svg"}
              alt="brand logo"
              style={{ width: "32px", height: "32px", marginTop: "-4px" }}
            />
            <span className="ms-2">medhive</span>*/}
          </Link>
          <div className="d-flex align-items-center ms-auto order-lg-2">
            <button
              className="btn btn-light-lite px-3"
              onClick={handleThemeToggle}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill text-dark"></i>
              )}
            </button>
            <Link to="/login" className="btn btn-sm btn-primary ms-2">Login</Link>
            <button
              className="navbar-toggler btn-sm ms-2"
              type="button"
              aria-controls="mainNavbar"
              aria-expanded={navbarExpanded}
              aria-label="Toggle navigation"
              onClick={() => setNavbarExpanded((prev) => !prev)}
            >
              <span><i className={navbarShouldBeScrolled ? "bi bi-list" : "bi bi-list opacity-50"}></i></span>
            </button>
          </div>
          <div className={`collapse navbar-collapse justify-content-center order-lg-1${navbarExpanded ? ' show' : ''}`} id="mainNavbar">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4 gap-2 align-items-lg-center text-center">
              <li className="nav-item">
                <Link to="/" className="nav-link" onClick={() => setNavbarExpanded(false)}>Home</Link>
              </li>
              <li className="nav-item">
                <a href="#features" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Features</a>
              </li>
              <li className="nav-item">
                <a href="#pricing" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Pricing</a>
              </li>
              <li className="nav-item">
                <Link to="/about" className="nav-link" onClick={() => setNavbarExpanded(false)}>About Us</Link>
              </li>
              <li className="nav-item">
                <Link to="/contact" className="nav-link" onClick={() => setNavbarExpanded(false)}>Contact Us</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="terms-header-section">
        {/* Hero Background Elements */}
        <div className="hero-background-elements">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '220px' }}>
            <div className="col-lg-12 text-center">
              <h1 className="display-4 fw-bold mb-3 gradient-text">Terms of Use</h1>
              <p className="lead text-muted">Last updated: {currentYear}. Please read these terms carefully before using our platform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="terms-container bg-light-card">
                
                {/* Introduction */}
                <div className="terms-section">
                  <h2>1. Introduction</h2>
                  <p>
                    Welcome to medhive. These Terms of Use ("Terms") govern your use of our website 
                    and services located at medhive.com (the "Service") operated by medhive Inc. 
                    ("medhive," "we," "us," or "our").
                  </p>
                  <p>
                    By accessing or using the Service, you agree to be bound by these Terms. If you 
                    disagree with any part of the terms, then you may not access the Service.
                  </p>
                </div>

                {/* Acceptance of Terms */}
                <div className="terms-section">
                  <h2>2. Acceptance of Terms</h2>
                  <p>
                    By using our Service, you confirm that you have read, understood, and agree to 
                    be bound by these Terms. If you are using the Service on behalf of an organization, 
                    you represent and warrant that you have the authority to bind that organization to 
                    these Terms.
                  </p>
                </div>

                {/* Description of Service */}
                <div className="terms-section">
                  <h2>3. Description of Service</h2>
                  <p>
                    medhive provides a comprehensive platform for medical education management, including:
                  </p>
                  <ul>
                    <li>Rotation scheduling and management</li>
                    <li>Evaluation systems and performance tracking</li>
                    <li>Work hour logging and compliance monitoring</li>
                    <li>Case log management and documentation</li>
                    <li>Communication and notification systems</li>
                  </ul>
                  <p>
                    The Service is designed for healthcare institutions, medical education programs, 
                    and their authorized users.
                  </p>
                </div>

                {/* User Accounts */}
                <div className="terms-section">
                  <h2>4. User Accounts</h2>
                  <p>
                    When you create an account with us, you must provide accurate, complete, and 
                    current information. You are responsible for safeguarding the password and for 
                    all activities that occur under your account.
                  </p>
                  <p>
                    You agree not to:
                  </p>
                  <ul>
                    <li>Use another user's account without permission</li>
                    <li>Create false or misleading account information</li>
                    <li>Share your account credentials with others</li>
                    <li>Use the Service for any unauthorized or illegal purpose</li>
                  </ul>
                </div>

                {/* Acceptable Use */}
                <div className="terms-section">
                  <h2>5. Acceptable Use</h2>
                  <p>
                    You agree to use the Service only for lawful purposes and in accordance with 
                    these Terms. You agree not to use the Service:
                  </p>
                  <ul>
                    <li>In any way that violates any applicable federal, state, local, or international law or regulation</li>
                    <li>To transmit, or procure the sending of, any advertising or promotional material</li>
                    <li>To impersonate or attempt to impersonate medhive, a medhive employee, or any other person</li>
                    <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service</li>
                  </ul>
                </div>

                {/* Privacy and Data Protection */}
                <div className="terms-section">
                  <h2>6. Privacy and Data Protection</h2>
                  <p>
                    Your privacy is important to us. Our Privacy Policy explains how we collect, 
                    use, and protect your information. By using the Service, you agree to the 
                    collection and use of information in accordance with our Privacy Policy.
                  </p>
                  <p>
                    As a healthcare platform, we are committed to maintaining the highest standards 
                    of data protection and comply with applicable healthcare privacy regulations 
                    including HIPAA.
                  </p>
                </div>

                {/* Intellectual Property */}
                <div className="terms-section">
                  <h2>7. Intellectual Property</h2>
                  <p>
                    The Service and its original content, features, and functionality are and will 
                    remain the exclusive property of medhive and its licensors. The Service is 
                    protected by copyright, trademark, and other laws.
                  </p>
                  <p>
                    You retain ownership of any content you submit to the Service, but you grant 
                    us a license to use, modify, and display that content in connection with 
                    providing the Service.
                  </p>
                </div>

                {/* Payment Terms */}
                <div className="terms-section">
                  <h2>8. Payment Terms</h2>
                  <p>
                    For paid services, you agree to pay all fees in accordance with the pricing 
                    and payment terms presented to you at the time of purchase. All fees are 
                    non-refundable except as expressly stated in these Terms.
                  </p>
                  <p>
                    We reserve the right to change our pricing with 30 days' notice. Your continued 
                    use of the Service after such changes constitutes acceptance of the new pricing.
                  </p>
                </div>

                {/* Termination */}
                <div className="terms-section">
                  <h2>9. Termination</h2>
                  <p>
                    We may terminate or suspend your account and bar access to the Service immediately, 
                    without prior notice or liability, under our sole discretion, for any reason 
                    whatsoever and without limitation, including but not limited to a breach of the Terms.
                  </p>
                  <p>
                    If you wish to terminate your account, you may simply discontinue using the Service 
                    or contact us to delete your account.
                  </p>
                </div>

                {/* Limitation of Liability */}
                <div className="terms-section">
                  <h2>10. Limitation of Liability</h2>
                  <p>
                    In no event shall medhive, nor its directors, employees, partners, agents, 
                    suppliers, or affiliates, be liable for any indirect, incidental, special, 
                    consequential, or punitive damages, including without limitation, loss of profits, 
                    data, use, goodwill, or other intangible losses, resulting from your use of the Service.
                  </p>
                </div>

                {/* Disclaimer */}
                <div className="terms-section">
                  <h2>11. Disclaimer</h2>
                  <p>
                    The information on the Service is provided on an "as is" basis. To the fullest 
                    extent permitted by law, this Company excludes all representations, warranties, 
                    conditions and terms whether express or implied.
                  </p>
                  <p>
                    While we strive to provide accurate and up-to-date information, we make no 
                    warranties about the completeness, reliability, and accuracy of this information.
                  </p>
                </div>

                {/* Governing Law */}
                <div className="terms-section">
                  <h2>12. Governing Law</h2>
                  <p>
                    These Terms shall be interpreted and governed by the laws of the State of 
                    Massachusetts, without regard to its conflict of law provisions. Our failure 
                    to enforce any right or provision of these Terms will not be considered a 
                    waiver of those rights.
                  </p>
                </div>

                {/* Changes to Terms */}
                <div className="terms-section">
                  <h2>13. Changes to Terms</h2>
                  <p>
                    We reserve the right, at our sole discretion, to modify or replace these Terms 
                    at any time. If a revision is material, we will try to provide at least 30 days 
                    notice prior to any new terms taking effect.
                  </p>
                  <p>
                    What constitutes a material change will be determined at our sole discretion.
                  </p>
                </div>

                {/* Contact Information */}
                <div className="terms-section">
                  <h2>14. Contact Information</h2>
                  <p>
                    If you have any questions about these Terms, please contact us at:
                  </p>
                  <div className="contact-info-box">
                    <p><strong>Email:</strong> legal@medhive.com</p>
                    <p><strong>Phone:</strong> 1-800-MEDHIVE</p>
                    <p><strong>Address:</strong> 123 Medical Center Dr., Suite 100, Boston, MA 02115</p>
                  </div>
                </div>

                {/* Effective Date */}
                <div className="terms-section">
                  <h2>15. Effective Date</h2>
                  <p>
                    These Terms are effective as of {currentYear} and will remain in effect except 
                    with respect to any changes in their provisions in the future, which will be 
                    in effect immediately after being posted on this page.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="terms-cta-section">
        <div className="container text-center">
          <h2 className="gradient-text mb-4">Questions About Our Terms?</h2>
          <p className="lead mb-4">Our legal team is here to help clarify any questions you may have</p>
          <div className="d-flex gap-3 justify-content-center">
            <Link to="/contact" className="btn btn-light btn-lg px-5 py-3">Contact Us</Link>
            <Link to="/privacy" className="btn btn-outline-light btn-lg px-5 py-3">Privacy Policy</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage; 